// Chemin: C:\gestion-copro\src\pages\coproprietaire\NouveauSignalementPage.tsx
// Formulaire de signalement pour copropriétaires
// Session 56 : création conforme au CADRAGE_SIGNALEMENTS_COPROPRIETAIRES.md
// - Localisation obligatoire (6 options)
// - Détection mots-clés privatifs (0 token, JS local)
// - Questions pattern pour défauts collectifs
// - Message éducatif permanent

import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Info,
  Upload,
  X,
  Camera,
  Send,
  ArrowLeft,
  AlertCircle,
  CheckCircle,
  Loader2,
  HelpCircle
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCopropriete } from '../../context/CoproprieteContext';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../services/firebase';
import { compressImage } from '../../utils/imageCompressor';

// ============================================
// CONFIGURATION DÉTECTION MOTS-CLÉS (0 TOKEN)
// ============================================

const MOTS_CLES_PRIVATIFS = [
  // Lot privatif
  "mon appartement", "ma cuisine", "ma salle de bain", "ma chambre",
  "mon balcon privatif", "ma terrasse", "chez moi", "dans mon logement",
  "ma chaudière", "mon radiateur", "ma serrure", "mon volet",
  "mon carrelage", "ma peinture", "mon plafond", "mon sol",
  "ma porte d'entrée", "ma fenêtre", "mon store",
  // Conflits personnels
  "mon voisin", "ma voisine", "voisin du dessus", "voisin du dessous",
  "dispute", "conflit", "insulte", "menace", "agression",
  "bruit de pas", "talons", "il me regarde", "elle me regarde",
  // Locataire
  "mon locataire", "mon propriétaire", "le locataire", "le propriétaire"
];

// Options de localisation conformes au cadrage
const OPTIONS_LOCALISATION = [
  {
    value: 'parties_communes_interieures',
    label: 'Parties communes intérieures',
    description: 'Hall, escaliers, couloirs, cave commune...',
    legitime: true
  },
  {
    value: 'parties_communes_exterieures',
    label: 'Parties communes extérieures',
    description: 'Parking, jardin, façade, toiture...',
    legitime: true
  },
  {
    value: 'equipement_collectif',
    label: 'Équipement collectif',
    description: 'Ascenseur, interphone, chauffage collectif, VMC, portail...',
    legitime: true
  },
  {
    value: 'malfacon_construction',
    label: 'Malfaçon construction - parties communes',
    description: 'Défaut lié à la construction (GPA/décennale)',
    legitime: true
  },
  {
    value: 'syndic_prestataire',
    label: 'Syndic ou prestataire',
    description: 'Problème avec le syndic ou un prestataire de la copropriété',
    legitime: true
  },
  {
    value: 'autre',
    label: 'Autre',
    description: 'Autre sujet ne correspondant pas aux catégories ci-dessus',
    legitime: false
  }
];

const NouveauSignalementPage: React.FC = () => {
  const navigate = useNavigate();
  const { firebaseUser, userProfile } = useAuth();
  const { copropriete } = useCopropriete();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // État du formulaire
  const [localisation, setLocalisation] = useState('');
  const [titre, setTitre] = useState('');
  const [description, setDescription] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  
  // Questions pattern (détection défauts collectifs)
  const [autresConcernes, setAutresConcernes] = useState<string | null>(null);
  const [communicationRecue, setCommunicationRecue] = useState<string | null>(null);
  const [problemeDesLivraison, setProblemeDesLivraison] = useState<string | null>(null);

  // États UI
  const [loading, setLoading] = useState(false);
  const [showAlerteAutre, setShowAlerteAutre] = useState(false);
  const [showAlertePrivatif, setShowAlertePrivatif] = useState(false);
  const [motsDetectes, setMotsDetectes] = useState<string[]>([]);
  const [etape, setEtape] = useState(1); // 1: localisation, 2: détails, 3: confirmation

  // ============================================
  // DÉTECTION MOTS-CLÉS PRIVATIFS (LOCAL)
  // ============================================
  
  const detecterMotsPrivatifs = (texte: string): string[] => {
    const texteLower = texte.toLowerCase();
    const motsDetectes: string[] = [];
    
    MOTS_CLES_PRIVATIFS.forEach(mot => {
      if (texteLower.includes(mot.toLowerCase())) {
        motsDetectes.push(mot);
      }
    });
    
    return motsDetectes;
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newDescription = e.target.value;
    setDescription(newDescription);
    
    // Détection en temps réel
    const mots = detecterMotsPrivatifs(newDescription);
    setMotsDetectes(mots);
  };

  // ============================================
  // GESTION DES PHOTOS
  // ============================================

  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newPhotos: File[] = [];
    const newPreviews: string[] = [];

    for (let i = 0; i < files.length && photos.length + newPhotos.length < 5; i++) {
      const file = files[i];
      if (file.type.startsWith('image/')) {
        try {
          // Compression de l'image
          const compressedFile = await compressImage(file);
          newPhotos.push(compressedFile);
          
          // Prévisualisation
          const reader = new FileReader();
          reader.onloadend = () => {
            newPreviews.push(reader.result as string);
            if (newPreviews.length === newPhotos.length) {
              setPhotoPreviews(prev => [...prev, ...newPreviews]);
            }
          };
          reader.readAsDataURL(compressedFile);
        } catch (error) {
          console.error('Erreur compression image:', error);
        }
      }
    }

    setPhotos(prev => [...prev, ...newPhotos]);
  };

  const removePhoto = (index: number) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
    setPhotoPreviews(prev => prev.filter((_, i) => i !== index));
  };

  // ============================================
  // NAVIGATION ÉTAPES
  // ============================================

  const handleLocalisationSelect = (value: string) => {
    setLocalisation(value);
    
    if (value === 'autre') {
      setShowAlerteAutre(true);
    } else {
      setShowAlerteAutre(false);
      setEtape(2);
    }
  };

  const continuerMalgreAutre = () => {
    setShowAlerteAutre(false);
    setEtape(2);
  };

  const handleSuivant = () => {
    // Vérifier si mots privatifs détectés
    if (motsDetectes.length > 0 && !showAlertePrivatif) {
      setShowAlertePrivatif(true);
      return;
    }
    
    setEtape(3);
  };

  const soumettreQuandMeme = () => {
    setShowAlertePrivatif(false);
    setEtape(3);
  };

  // ============================================
  // SOUMISSION DU SIGNALEMENT
  // ============================================

  const handleSubmit = async () => {
    if (!firebaseUser?.uid || !copropriete?.id || !userProfile) {
      alert('Erreur: données utilisateur manquantes');
      return;
    }

    if (!titre.trim() || !description.trim() || !localisation) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);

    try {
      // Upload des photos
      const photoUrls: string[] = [];
      for (const photo of photos) {
        const photoRef = ref(
          storage,
          `coproprietes/${copropriete.id}/signalements/${Date.now()}_${photo.name}`
        );
        await uploadBytes(photoRef, photo);
        const url = await getDownloadURL(photoRef);
        photoUrls.push(url);
      }

      // Créer le signalement
      const signalementData = {
        coproprieteId: copropriete.id,
        userId: firebaseUser.uid,
        declarant: {
          userId: firebaseUser.uid,
          nom: `${userProfile.prenom} ${userProfile.nom}`,
          email: userProfile.email,
          lot: userProfile.lots?.[0]?.numero || null
        },
        titre: titre.trim(),
        description: description.trim(),
        localisation: localisation,
        localisationDetail: OPTIONS_LOCALISATION.find(o => o.value === localisation)?.label || localisation,
        categorie: localisation,
        urgence: 'moyenne',
        photos: photoUrls,
        statut: 'nouveau',
        
        // Métadonnées détection
        alertePrivatifAffichee: motsDetectes.length > 0,
        motsPrivatifsDetectes: motsDetectes,
        
        // Réponses questions pattern
        questionsPattern: {
          autresConcernes: autresConcernes,
          communicationRecue: communicationRecue,
          problemeDesLivraison: problemeDesLivraison
        },
        
        // Flags pour le secrétaire CS
        flagPotentielCollectif: autresConcernes === 'oui' || communicationRecue === 'oui',
        flagMalfaconGPA: problemeDesLivraison === 'oui',
        
        historique: [{
          date: Timestamp.now(),
          action: 'Signalement créé',
          details: 'Soumis par le copropriétaire',
          auteurId: firebaseUser.uid,
          auteurNom: `${userProfile.prenom} ${userProfile.nom}`
        }],
        
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      await addDoc(collection(db, 'signalements'), signalementData);
      
      // Redirection vers confirmation
      navigate('/espace-copro/mes-signalements', { 
        state: { success: true, message: 'Votre signalement a été envoyé au Conseil Syndical.' }
      });

    } catch (error) {
      console.error('Erreur soumission signalement:', error);
      alert('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // RENDU
  // ============================================

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Bouton retour */}
      <button
        onClick={() => navigate('/espace-copro')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Retour au tableau de bord
      </button>

      {/* Titre */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Nouveau signalement</h1>
        <p className="text-gray-600 mt-1">
          Signalez un problème touchant les parties communes
        </p>
      </div>

      {/* Message éducatif permanent */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-2">Le saviez-vous ?</p>
            <p className="mb-2">
              Le Conseil Syndical défend les intérêts <strong>collectifs</strong> de la copropriété. 
              Il ne peut pas intervenir sur :
            </p>
            <ul className="list-disc list-inside space-y-1 text-blue-700">
              <li>Les problèmes dans votre appartement → contactez un artisan</li>
              <li>Les conflits de voisinage → consultez un médiateur</li>
              <li>Vos démarches GPA personnelles → contactez le promoteur</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Indicateur d'étapes */}
      <div className="flex items-center gap-2">
        {[1, 2, 3].map((step) => (
          <React.Fragment key={step}>
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                etape >= step
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-200 text-gray-500'
              }`}
            >
              {etape > step ? <CheckCircle className="w-5 h-5" /> : step}
            </div>
            {step < 3 && (
              <div className={`flex-1 h-1 ${etape > step ? 'bg-green-600' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* ============================================ */}
      {/* ÉTAPE 1 : LOCALISATION                      */}
      {/* ============================================ */}
      
      {etape === 1 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Où se situe le problème ?
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Sélectionnez la catégorie qui correspond le mieux à votre signalement.
          </p>

          <div className="space-y-3">
            {OPTIONS_LOCALISATION.map((option) => (
              <label
                key={option.value}
                className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-all ${
                  localisation === option.value
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="localisation"
                  value={option.value}
                  checked={localisation === option.value}
                  onChange={() => handleLocalisationSelect(option.value)}
                  className="mt-1 w-4 h-4 text-green-600 focus:ring-green-500"
                />
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{option.label}</p>
                  <p className="text-sm text-gray-500">{option.description}</p>
                </div>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Modal alerte "Autre" */}
      {showAlerteAutre && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Rappel important</h3>
            </div>
            
            <div className="text-gray-600 space-y-3 mb-6">
              <p>Le Conseil Syndical traite uniquement les sujets impactant :</p>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>Les parties communes</li>
                <li>Les équipements collectifs</li>
                <li>La gestion de la copropriété</li>
              </ul>
              <p className="text-sm">Il ne peut pas intervenir sur :</p>
              <ul className="list-disc list-inside text-sm space-y-1 text-gray-500">
                <li>Les problèmes dans votre appartement (lot privatif)</li>
                <li>Les conflits entre voisins</li>
                <li>Vos démarches personnelles avec le promoteur</li>
              </ul>
            </div>

            <p className="text-sm text-gray-600 mb-4">Souhaitez-vous continuer malgré tout ?</p>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setLocalisation('');
                  setShowAlerteAutre(false);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Modifier ma sélection
              </button>
              <button
                onClick={continuerMalgreAutre}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              >
                Continuer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* ÉTAPE 2 : DÉTAILS                           */}
      {/* ============================================ */}
      
      {etape === 2 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">
              Décrivez le problème
            </h2>
            <p className="text-sm text-gray-500">
              Catégorie : {OPTIONS_LOCALISATION.find(o => o.value === localisation)?.label}
            </p>
          </div>

          {/* Titre */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Titre du signalement *
            </label>
            <input
              type="text"
              value={titre}
              onChange={(e) => setTitre(e.target.value)}
              placeholder="Ex: Fuite d'eau dans le hall d'entrée"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              maxLength={100}
            />
            <p className="text-xs text-gray-400 mt-1">{titre.length}/100 caractères</p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description détaillée *
            </label>
            <textarea
              value={description}
              onChange={handleDescriptionChange}
              placeholder="Décrivez le problème de manière factuelle : où exactement, depuis quand, quelle est la gravité..."
              rows={5}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              maxLength={2000}
            />
            <p className="text-xs text-gray-400 mt-1">{description.length}/2000 caractères</p>
            
            {/* Alerte mots-clés détectés (affichage discret) */}
            {motsDetectes.length > 0 && (
              <div className="mt-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-orange-700">
                    Votre description semble mentionner un lot privatif ou un conflit de voisinage. 
                    Assurez-vous que votre signalement concerne bien les parties communes.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Photos */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Photos (facultatif mais recommandé)
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Ajoutez jusqu'à 5 photos pour illustrer le problème
            </p>

            <div className="flex flex-wrap gap-3">
              {photoPreviews.map((preview, index) => (
                <div key={index} className="relative w-20 h-20">
                  <img
                    src={preview}
                    alt={`Photo ${index + 1}`}
                    className="w-full h-full object-cover rounded-lg border border-gray-200"
                  />
                  <button
                    type="button"
                    onClick={() => removePhoto(index)}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              
              {photos.length < 5 && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-400 hover:border-green-500 hover:text-green-500 transition-colors"
                >
                  <Camera className="w-6 h-6" />
                  <span className="text-xs mt-1">Ajouter</span>
                </button>
              )}
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handlePhotoSelect}
              className="hidden"
            />
          </div>

          {/* Questions pattern (détection défauts collectifs) */}
          <div className="border-t border-gray-100 pt-6">
            <div className="flex items-center gap-2 mb-4">
              <HelpCircle className="w-5 h-5 text-gray-400" />
              <h3 className="font-medium text-gray-900">Questions complémentaires</h3>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Ces informations aident le Conseil Syndical à mieux qualifier votre signalement.
            </p>

            {/* Question 1 */}
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">
                D'autres copropriétaires vous ont-ils fait part du même problème ?
              </p>
              <div className="flex gap-3">
                {['oui', 'non', 'ne_sais_pas'].map((val) => (
                  <label
                    key={val}
                    className={`flex-1 px-4 py-2 border rounded-lg text-center cursor-pointer transition-all ${
                      autresConcernes === val
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="autresConcernes"
                      value={val}
                      checked={autresConcernes === val}
                      onChange={() => setAutresConcernes(val)}
                      className="sr-only"
                    />
                    {val === 'oui' ? 'Oui' : val === 'non' ? 'Non' : 'Je ne sais pas'}
                  </label>
                ))}
              </div>
            </div>

            {/* Question 2 */}
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">
                Avez-vous reçu une communication du syndic ou du promoteur sur ce sujet ?
              </p>
              <div className="flex gap-3">
                {['oui', 'non'].map((val) => (
                  <label
                    key={val}
                    className={`flex-1 px-4 py-2 border rounded-lg text-center cursor-pointer transition-all ${
                      communicationRecue === val
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="communicationRecue"
                      value={val}
                      checked={communicationRecue === val}
                      onChange={() => setCommunicationRecue(val)}
                      className="sr-only"
                    />
                    {val === 'oui' ? 'Oui' : 'Non'}
                  </label>
                ))}
              </div>
            </div>

            {/* Question 3 */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">
                Ce problème existait-il dès la livraison de votre logement ?
              </p>
              <div className="flex gap-3">
                {['oui', 'non', 'ne_sais_pas'].map((val) => (
                  <label
                    key={val}
                    className={`flex-1 px-4 py-2 border rounded-lg text-center cursor-pointer transition-all ${
                      problemeDesLivraison === val
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="problemeDesLivraison"
                      value={val}
                      checked={problemeDesLivraison === val}
                      onChange={() => setProblemeDesLivraison(val)}
                      className="sr-only"
                    />
                    {val === 'oui' ? 'Oui' : val === 'non' ? 'Non' : 'Je ne sais pas'}
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Boutons navigation */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => setEtape(1)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Retour
            </button>
            <button
              type="button"
              onClick={handleSuivant}
              disabled={!titre.trim() || !description.trim()}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continuer
            </button>
          </div>
        </div>
      )}

      {/* Modal alerte mots privatifs */}
      {showAlertePrivatif && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Attention</h3>
            </div>
            
            <div className="text-gray-600 space-y-3 mb-6">
              <p>
                Votre signalement semble concerner votre <strong>lot privatif</strong> ou 
                un <strong>conflit de voisinage</strong>.
              </p>
              <p className="text-sm">
                Le Conseil Syndical traite uniquement les problèmes touchant les parties communes.
              </p>
              {motsDetectes.length > 0 && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Termes détectés :</p>
                  <p className="text-sm text-gray-700">{motsDetectes.join(', ')}</p>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowAlertePrivatif(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Modifier mon signalement
              </button>
              <button
                onClick={soumettreQuandMeme}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              >
                Soumettre quand même
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* ÉTAPE 3 : CONFIRMATION                      */}
      {/* ============================================ */}
      
      {etape === 3 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
          <h2 className="text-lg font-semibold text-gray-900">
            Récapitulatif de votre signalement
          </h2>

          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500 mb-1">Catégorie</p>
              <p className="font-medium text-gray-900">
                {OPTIONS_LOCALISATION.find(o => o.value === localisation)?.label}
              </p>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500 mb-1">Titre</p>
              <p className="font-medium text-gray-900">{titre}</p>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500 mb-1">Description</p>
              <p className="text-gray-900 whitespace-pre-wrap">{description}</p>
            </div>

            {photos.length > 0 && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500 mb-2">{photos.length} photo(s) jointe(s)</p>
                <div className="flex gap-2">
                  {photoPreviews.map((preview, index) => (
                    <img
                      key={index}
                      src={preview}
                      alt={`Photo ${index + 1}`}
                      className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Indicateurs pour le CS */}
            {(autresConcernes === 'oui' || communicationRecue === 'oui' || problemeDesLivraison === 'oui') && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm font-medium text-blue-800 mb-2">
                  Informations complémentaires transmises au CS :
                </p>
                <ul className="text-sm text-blue-700 space-y-1">
                  {autresConcernes === 'oui' && (
                    <li>• D'autres copropriétaires sont concernés</li>
                  )}
                  {communicationRecue === 'oui' && (
                    <li>• Une communication officielle a été reçue</li>
                  )}
                  {problemeDesLivraison === 'oui' && (
                    <li>• Problème existant depuis la livraison (GPA potentielle)</li>
                  )}
                </ul>
              </div>
            )}
          </div>

          {/* Boutons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => setEtape(2)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Modifier
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Envoyer mon signalement
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NouveauSignalementPage;
